/**
 * @class
 * @function Pagination
 */
if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();

      const id = this.dataset.section;

      this.button = this.querySelector('.load-more');

      this.grid = document.querySelector(
        `#product-grid[data-id="${id}"]`
      );

      this.animations_enabled =
        document.body.classList.contains('animations-true') &&
        typeof gsap !== 'undefined';

      this.i = 2;

      this.page_size = parseInt(
        this.dataset.pageSize,
        10
      );

      this.observer = null;
    }

    connectedCallback() {
      if (!this.grid) {
        console.warn(
          'Pagination: product grid not found for section',
          this.dataset.section
        );

        return;
      }

      if (
        this.classList.contains(
          'pagination-type--loadmore'
        )
      ) {
        this.loadMore();
      }

      if (
        this.classList.contains(
          'pagination-type--infinite'
        )
      ) {
        this.infinite();
      }
    }

    addUrlParam(search, key) {
      const newParam = `${key}=${this.i}`;

      let params = `?${newParam}`;

      if (search) {
        params = search.replace(
          new RegExp(`([?&])${key}[^&]*`),
          `$1${newParam}`
        );

        if (params === search) {
          params += `&${newParam}`;
        }
      }

      return params;
    }

    loadMore() {
      if (!this.button) return;

      this.button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          this.loadProducts();

          this.button.blur();
        }
      );
    }

    infinite() {
      this.observer =
        new IntersectionObserver(
          (entries) => {
            const entry = entries[0];

            if (
              entry &&
              entry.isIntersecting &&
              !this.hasAttribute('loading')
            ) {
              this.loadProducts();
            }
          },
          {
            root: null,

            /*
             * Start loading the next page before the
             * customer reaches the absolute bottom.
             */
            rootMargin: '300px 0px',

            threshold: 0
          }
        );

      this.observer.observe(this);
    }

    loadProducts() {
      if (this.hasAttribute('loading')) {
        return;
      }

      const url =
        document.location.pathname +
        this.addUrlParam(
          document.location.search,
          'page'
        );

      this.setAttribute('loading', '');

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Pagination request failed: ${response.status}`
            );
          }

          return response.text();
        })

        .then((responseText) => {
          this.renderProducts(
            responseText,
            url
          );

          dispatchCustomEvent(
            'pagination:page-changed',
            {
              url
            }
          );

          /*
           * Only move to the following page once the
           * current request has succeeded.
           */
          this.i++;
        })

        .catch((error) => {
          console.error(
            'Pagination error:',
            error
          );

          this.removeAttribute('loading');
        });
    }

    renderProducts(html, url) {
      const parsedHTML =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const gridToReplace =
        parsedHTML.getElementById(
          'product-grid'
        );

      /*
       * No product grid means that there is no
       * following page to load.
       */
      if (!gridToReplace) {
        this.stopPagination();

        return;
      }

      /*
       * COPA CUSTOMIZATION
       *
       * Take every column from the incoming page.
       *
       * This deliberately includes:
       * - normal product cards
       * - promotion blocks
       *
       * Otherwise promotions positioned on later
       * pages disappear when using infinite scroll.
       */
      const allItems =
        Array.from(
          gridToReplace.children
        ).filter((item) =>
          item.classList.contains('column')
        );

      /*
       * COPA CUSTOMIZATION
       *
       * Promotion blocks are appended but do not
       * count towards Shopify's products-per-page
       * calculation.
       */
      const products =
        allItems.filter(
          (item) =>
            !item.classList.contains(
              'promotion-column'
            )
        );

      /*
       * If Shopify returned an empty grid, stop.
       */
      if (allItems.length === 0) {
        this.stopPagination();

        return;
      }

      const appendItems = () => {
        allItems.forEach((item) => {
          this.grid.appendChild(item);

          /*
           * Force newly appended lazy images to load.
           */
          item
            .querySelectorAll('.lazyload')
            .forEach((image) => {
              if (
                typeof lazySizes !==
                  'undefined' &&
                lazySizes.loader
              ) {
                lazySizes.loader.unveil(
                  image
                );
              }
            });
        });
      };

      if (this.animations_enabled) {
        /*
         * With GSAP enabled we append directly.
         *
         * Running view transitions and GSAP on the
         * same inserted elements can cause flicker.
         */
        appendItems();

        gsap.set(allItems, {
          opacity: 0,
          y: 30
        });

        gsap.to(allItems, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,

          onComplete: () => {
            this.removeAttribute(
              'loading'
            );

            this.afterProductsLoaded();
          }
        });
      } else {
        /*
         * Preserve Theme 17's view-transition
         * behavior when GSAP isn't running.
         */
        if (
          typeof withViewTransition ===
          'function'
        ) {
          withViewTransition(
            appendItems
          );
        } else {
          appendItems();
        }

        this.removeAttribute('loading');

        this.afterProductsLoaded();
      }

      /*
       * IMPORTANT:
       *
       * Decide whether there is another page using
       * actual product count only.
       *
       * Promotion columns must NOT influence this.
       */
      if (
        Number.isFinite(this.page_size) &&
        products.length <
          this.page_size
      ) {
        this.stopPagination();
      }
    }

    afterProductsLoaded() {
      /*
       * Reload Stamped review badges for products
       * inserted dynamically.
       */
      if (
        typeof StampedFn !==
          'undefined' &&
        typeof StampedFn.loadBadges ===
          'function'
      ) {
        StampedFn.loadBadges();
      }

      /*
       * Notify any custom COPA scripts that the
       * product grid has changed.
       */
      window.dispatchEvent(
        new CustomEvent(
          'copa:product-grid-updated'
        )
      );
    }

    stopPagination() {
      this.removeAttribute('loading');

      if (this.observer) {
        this.observer.unobserve(this);
        this.observer.disconnect();

        this.observer = null;
      }

      if (this.button) {
        this.button.style.display =
          'none';

        this.button.classList.add(
          'visually-hidden'
        );
      }

      /*
       * Hide the pagination element itself for
       * infinite pagination after the final page.
       */
      if (
        this.classList.contains(
          'pagination-type--infinite'
        )
      ) {
        this.style.display = 'none';
      }
    }

    hide() {
      this.stopPagination();
    }
  }

  customElements.define(
    'pagination-theme',
    Pagination
  );
}